/**
 * WHEP player for the fleet console's video wall.
 *
 * Media path (docs/design/design-hub-video-fanout-20260801.md):
 *
 *   dog go2rtc (encodes ONCE)
 *     --RTSP over the private net, 1 copy--> relay on panda02
 *       --WHEP signalling via hub /api/v1/whep/{device}/{stream}--> N browsers
 *       --WebRTC media, browser <=> relay direct-------------------> N browsers
 *
 * The dog's cellular uplink therefore carries ONE copy no matter how many people
 * watch. That is the whole point of the relay: before it, go2rtc opened a separate
 * peer connection per browser, so two viewers doubled the uplink and starved the
 * cockpit's own control link (measured 2026-08-01: 780 KB/s up => 476 ms ping =>
 * remote cockpit usable 26/71 attempts; video off => 12 KB/s, 43.6 ms, 20/20).
 *
 * ★ LAZY BY CONSTRUCTION. A peer connection is opened only while a tile is
 *   actually mounted and `enabled`. Nobody watching => the relay pulls nothing =>
 *   the dog uploads nothing. Never "connect everything up front and hide the
 *   tiles" — that spends the exact resource this design exists to protect.
 *
 * ★★ EVERY NUMBER HERE IS MEASURED (CLAUDE.md §9b). The prototype's tiles showed
 *    "28ms / 30fps / direct" as literal constants, and the freeze warning was a
 *    mock field. In this version fps/kbps/resolution/rtt/loss come from
 *    RTCPeerConnection.getStats(), and freeze is "framesDecoded stopped advancing
 *    for FREEZE_MS while the connection is still up" — an observation, not a flag.
 *    A stat we cannot read stays `null`, and the UI must render null as "—".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '@/lib/hubClient';
import { filterIceCandidates } from '@/lib/iceFilter';
import { classifyWhepResponse } from '@/lib/whepVerdict';

const BASE = (import.meta.env.VITE_HUB_BASE ?? '').replace(/\/$/, '');

/**
 * Why each state exists as its own value instead of one boolean:
 *
 *   idle          not asked for yet (tile disabled) — the dog is NOT uploading
 *   connecting    offer sent / ICE in progress
 *   playing       frames are arriving
 *   frozen        connected, but framesDecoded has not moved for FREEZE_MS.
 *                 §9b again: "the last frame is still on screen" must not read as
 *                 "alive". The dog dropping off the private net looks exactly like
 *                 this, and a still picture is the most convincing lie available.
 *   nosignal      the relay answered, but no media came (source not producing)
 *   unconfigured  hub says 503 — no relay deployed. A deployment gap, not a fault.
 *   unreachable   hub says 502 — relay is down / cannot reach the device.
 *   denied        401/403 — token expired. Retrying forever would hide it.
 */
export type WhepState =
  | 'idle' | 'connecting' | 'playing' | 'frozen'
  | 'nosignal' | 'unconfigured' | 'unreachable' | 'denied';

export interface WhepStats {
  /** Decoded frames per second, averaged over the last sample interval. */
  fps: number | null;
  /** Inbound video bitrate in kbit/s. */
  kbps: number | null;
  width: number | null;
  height: number | null;
  /** ICE round-trip time in ms — the real link latency, not a guess. */
  rttMs: number | null;
  /** Fraction of packets lost, 0..1. */
  loss: number | null;
  /** Seconds since the first frame of this session. */
  uptimeS: number | null;
}

/** The handful of getStats counters this hook reads. See sample() for why. */
interface StatsEntry {
  type: string;
  kind?: string;
  state?: string;
  framesDecoded?: number;
  frameWidth?: number;
  frameHeight?: number;
  bytesReceived?: number;
  packetsLost?: number;
  packetsReceived?: number;
  currentRoundTripTime?: number;
}

const EMPTY_STATS: WhepStats = { fps: null, kbps: null, width: null, height: null, rttMs: null, loss: null, uptimeS: null };

/** How long framesDecoded may stand still before we call it frozen. */
const FREEZE_MS = 4000;
/** Stats sampling period. Also the freeze detector's resolution. */
const SAMPLE_MS = 1000;
/** Reconnect backoff after a failed attempt. */
const RETRY_MS = 5000;

function whepUrl(device: string, stream: string): string {
  return `${BASE}/api/v1/whep/${encodeURIComponent(device)}/${encodeURIComponent(stream)}`;
}

/**
 * Ask the hub for an SDP answer. The verdict logic lives in lib/whepVerdict.ts
 * (status codes + the "200 HTML is not an SDP" trap) so it can be tested without
 * a browser; this function only does the transport.
 */
async function askWhep(device: string, stream: string, offerSdp: string):
  Promise<{ sdp: string } | { error: WhepState; detail: string }> {
  let r: Response;
  try {
    r = await fetch(whepUrl(device, stream), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/sdp',
        ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}),
      },
      body: offerSdp,
    });
  } catch (e) {
    return { error: 'unreachable', detail: e instanceof Error ? e.message : String(e) };
  }
  const v = classifyWhepResponse(r.status, await r.text());
  return v.kind === 'answer' ? { sdp: v.sdp } : { error: v.kind, detail: v.detail };
}

/**
 * One WHEP session. `enabled === false` tears the peer connection down and never
 * opens one, so a hidden or switched-off tile costs the dog nothing.
 */
export function useWhep(device: string, stream: string, enabled: boolean) {
  const [state, setState] = useState<WhepState>('idle');
  const [detail, setDetail] = useState<string | null>(null);
  const [stats, setStats] = useState<WhepStats>(EMPTY_STATS);
  // Retry nonce. It has to be an effect DEPENDENCY, not just a state change:
  // setState alone would not re-run the effect, so "retry" would silently do
  // nothing — a button that looks like it works is worse than no button.
  const [nonce, setNonce] = useState(0);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const mediaRef = useRef<MediaStream | null>(null);

  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    videoElRef.current = el;
    if (el && mediaRef.current) {
      el.srcObject = mediaRef.current;
      el.play().catch(() => { /* autoplay policy: muted video is allowed */ });
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState('idle');
      setDetail(null);
      setStats(EMPTY_STATS);
      mediaRef.current = null;
      return;
    }
    let pc: RTCPeerConnection | null = null;
    let stopped = false;
    let retryTimer: number | undefined;
    let sampleTimer: number | undefined;
    // Freeze/rate detection state — all of it derived from getStats deltas.
    let lastFrames = 0;
    let lastFramesAt = 0;
    let lastBytes = 0;
    let lastBytesAt = 0;
    let firstFrameAt = 0;

    const cleanup = () => {
      if (sampleTimer) { clearInterval(sampleTimer); sampleTimer = undefined; }
      if (pc) { pc.close(); pc = null; }
    };

    const scheduleRetry = (s: WhepState, why: string) => {
      if (stopped) return;
      setState(s);
      setDetail(why);
      setStats(EMPTY_STATS);
      cleanup();
      // 'denied' and 'unconfigured' are not transient: retrying every 5 s would
      // bury an expired token / missing deployment under a spinner. Escape hatch
      // (CLAUDE.md §10): the tile still offers a manual retry, so this is a stop,
      // not a dead end.
      if (s === 'denied' || s === 'unconfigured') return;
      retryTimer = window.setTimeout(connect, RETRY_MS);
    };

    const sample = async () => {
      if (!pc || stopped) return;
      let report: RTCStatsReport;
      try { report = await pc.getStats(); } catch { return; }
      const now = performance.now();
      const next: WhepStats = { ...EMPTY_STATS };
      let framesDecoded: number | null = null;

      report.forEach(raw => {
        // Narrow to the fields we actually read instead of naming lib.dom's stats
        // interfaces: those names are types only (eslint's no-undef rejects them)
        // and their shape drifts between TS DOM lib versions, while these five
        // counters have been stable since the stats spec settled.
        const s = raw as StatsEntry;
        if (s.type === 'inbound-rtp' && s.kind === 'video') {
          const v = s;
          if (typeof v.framesDecoded === 'number') framesDecoded = v.framesDecoded;
          if (typeof v.frameWidth === 'number') next.width = v.frameWidth;
          if (typeof v.frameHeight === 'number') next.height = v.frameHeight;
          if (typeof v.bytesReceived === 'number' && lastBytesAt > 0) {
            const dt = (now - lastBytesAt) / 1000;
            if (dt > 0) next.kbps = Math.max(0, ((v.bytesReceived - lastBytes) * 8) / 1000 / dt);
          }
          if (typeof v.bytesReceived === 'number') { lastBytes = v.bytesReceived; lastBytesAt = now; }
          const lost = v.packetsLost ?? 0;
          const recv = (v.packetsReceived ?? 0) + lost;
          // A ratio without its denominator is not a measurement (§9b): with no
          // packets yet there is no loss rate, so it stays null rather than 0.
          if (recv > 0) next.loss = lost / recv;
        }
        if (s.type === 'candidate-pair' && s.state === 'succeeded') {
          if (typeof s.currentRoundTripTime === 'number') next.rttMs = s.currentRoundTripTime * 1000;
        }
      });

      if (framesDecoded !== null) {
        if (framesDecoded > lastFrames) {
          if (firstFrameAt === 0) firstFrameAt = now;
          if (lastFramesAt > 0) {
            const dt = (now - lastFramesAt) / 1000;
            if (dt > 0) next.fps = (framesDecoded - lastFrames) / dt;
          }
          lastFrames = framesDecoded;
          lastFramesAt = now;
          if (!stopped) setState('playing');
        } else if (lastFramesAt > 0 && now - lastFramesAt > FREEZE_MS) {
          // Connected but no new frames: say so. This is the state the prototype
          // faked with a `freezeSince` field and the one that matters most on site.
          if (!stopped) setState('frozen');
        }
      }
      if (firstFrameAt > 0) next.uptimeS = (now - firstFrameAt) / 1000;
      // fps/kbps keep their previous value when this sample could not derive one,
      // so the tile does not flicker between a number and "—" every second.
      setStats(prev => ({
        ...next,
        fps: next.fps ?? prev.fps,
        kbps: next.kbps ?? prev.kbps,
        width: next.width ?? prev.width,
        height: next.height ?? prev.height,
        rttMs: next.rttMs ?? prev.rttMs,
      }));
    };

    const connect = async () => {
      if (stopped) return;
      setState('connecting');
      setDetail(null);
      try {
        pc = new RTCPeerConnection();
        pc.addTransceiver('video', { direction: 'recvonly' });
        pc.addTransceiver('audio', { direction: 'recvonly' });
        pc.ontrack = e => {
          if (!e.streams[0]) return;
          mediaRef.current = e.streams[0];
          if (videoElRef.current) {
            videoElRef.current.srcObject = e.streams[0];
            videoElRef.current.play().catch(() => { /* noop */ });
          }
        };
        pc.onconnectionstatechange = () => {
          if (!pc) return;
          if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
            scheduleRetry('nosignal', `WebRTC ${pc.connectionState}`);
          }
        };
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const answer = await askWhep(device, stream, offer.sdp ?? '');
        if (stopped) return;
        if ('error' in answer) { scheduleRetry(answer.error, answer.detail); return; }
        // Keep only candidates this browser can actually reach — see lib/iceFilter.ts
        // for the two black-screen incidents that shaped this line.
        const sdp = filterIceCandidates(answer.sdp, window.location.hostname);
        await pc.setRemoteDescription({ type: 'answer', sdp });
        sampleTimer = window.setInterval(sample, SAMPLE_MS);
        // Relay answered but the source may still produce nothing (dog off the
        // private net). Until a frame is decoded this is 'connecting', and the
        // freeze detector below turns a never-arriving first frame into
        // 'nosignal' — we never claim a picture we have not counted frames for.
        window.setTimeout(() => {
          if (!stopped && lastFrames === 0) setState(s => (s === 'connecting' ? 'nosignal' : s));
        }, FREEZE_MS * 2);
      } catch (e) {
        scheduleRetry('nosignal', e instanceof Error ? e.message : String(e));
      }
    };

    connect();
    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      cleanup();
      mediaRef.current = null;
    };
  }, [device, stream, enabled, nonce]);

  /** Manual retry — the escape hatch for the two states that stop auto-retrying. */
  const retry = useCallback(() => setNonce(n => n + 1), []);

  return { state, detail, stats, attachVideo, retry };
}
